import { create } from 'zustand';
import { authTokensSchema, type UserDto } from '@tg-hookah/shared/dto';
import { api, configureApi } from '@/lib/api';
import { readInitData } from '@/lib/tg';

interface AuthState {
  user: UserDto | null;
  accessToken: string | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  error: string | null;
  loginWithTelegram: () => Promise<void>;
  logout: () => void;
  setAccessToken: (token: string | null) => void;
  setUser: (user: UserDto | null) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  status: 'idle',
  error: null,
  setAccessToken: (token) => set({ accessToken: token }),
  setUser: (user) => set({ user }),
  logout: () => set({ user: null, accessToken: null, status: 'unauthenticated' }),
  loginWithTelegram: async () => {
    const initData = readInitData();
    if (!initData) {
      set({ status: 'unauthenticated', error: 'no_init_data' });
      return;
    }
    set({ status: 'loading', error: null });
    try {
      const res = await api.post('/auth/telegram', { initData });
      const parsed = authTokensSchema.parse(res.data);
      set({
        user: parsed.user,
        accessToken: parsed.accessToken,
        status: 'authenticated',
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'auth_failed';
      set({ status: 'error', error: message });
    }
  },
}));

// Wire axios <-> auth store once.
configureApi({
  getAccessToken: () => useAuthStore.getState().accessToken,
  setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
  onLogout: () => useAuthStore.getState().logout(),
});
